import { useState } from "react";
import { Dialog, DialogContent, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface RemoveRecordDialogProps {
	open: boolean;
	petName: string;
	chip: string;
	email: string;
	onCancel: () => void;
	onDone: () => void;
}

declare global {
	interface Window {
		_cio: any;
	}
}

function loadCio() {
	if (document.getElementById("cio-tracker")) return;

	const _cio = (window._cio = window._cio || []);
	const stub = (f: string) => (...args: any[]) => _cio.push([f, ...args]);
	for (const method of ["load", "identify", "sidentify", "track", "page"]) {
		_cio[method] = stub(method);
	}

	const t = document.createElement("script");
	t.async = true;
	t.id = "cio-tracker";
	t.setAttribute("data-site-id", process.env.VITE_CIO_SITE_ID || "");
	t.setAttribute("data-use-array-params", "true");
	t.setAttribute("data-auto-track-page", "false");
	t.src = "https://assets.customer.io/assets/track.js";
	document.head.appendChild(t);
}

function RemoveRecordDialog({ open, petName, chip, email, onCancel, onDone }: RemoveRecordDialogProps) {
	const [step, setStep] = useState<"confirm" | "success">("confirm");

	function handleRemove() {
		if (email) {
			loadCio();
			window._cio.identify({ id: email, pet_record_removed: true });
		}
		setStep("success");
	}

	function handleCancel() {
		setStep("confirm");
		onCancel();
	}

	function handleDone() {
		setStep("confirm");
		onDone();
	}

	return (
		<Dialog
			open={open}
			onClose={step === "confirm" ? handleCancel : handleDone}
			maxWidth="sm"
			fullWidth
			PaperProps={{ sx: { borderRadius: 3, px: 2, py: 2, maxWidth: 480 } }}
		>
			<DialogContent sx={{ p: 2 }}>
				{step === "confirm" ? (
					<div className="flex flex-col gap-5">
						{/* Header */}
						<div className="flex items-center justify-between">
							<Typography variant="h5" fontWeight={700}>
								Confirm
							</Typography>
							<IconButton onClick={handleCancel} size="small">
								<CloseIcon />
							</IconButton>
						</div>

						{/* Body */}
						<Typography variant="body1">
							Are you sure you want to remove this pet record from future activation messages?
						</Typography>

						{/* Pet Record Card */}
						<div style={{ border: "1px solid #ccc", borderRadius: 8, padding: 16 }}>
							<div className="flex flex-col gap-1">
								<Typography variant="body2" fontWeight={700} sx={{ fontSize: 15 }}>
									Pet Record
								</Typography>
								<Typography variant="body2" sx={{ fontSize: 15 }}>
									Pet: <strong>{petName}</strong>
								</Typography>
								<Typography variant="body2" sx={{ fontSize: 15 }}>
									Microchip ID: <strong>{chip}</strong>
								</Typography>
							</div>
						</div>

						{/* Buttons */}
						<div className="flex flex-col gap-3">
							<button
								onClick={handleRemove}
								style={{
									border: "2px solid #FF9500",
									color: "#FF9500",
									background: "transparent",
									borderRadius: 9999,
									padding: "12px 24px",
									fontWeight: 700,
									fontSize: 16,
									cursor: "pointer",
								}}
							>
								Yes, Remove Record
							</button>
							<button
								onClick={handleCancel}
								style={{
									border: "none",
									background: "#FF9500",
									color: "#fff",
									borderRadius: 9999,
									padding: "12px 24px",
									fontWeight: 700,
									fontSize: 16,
									cursor: "pointer",
								}}
							>
								No, Keep Record
							</button>
						</div>
					</div>
				) : (
					<div className="flex flex-col gap-5">
						{/* Header */}
						<div className="flex items-center justify-between">
							<Typography variant="h5" fontWeight={700}>
								Record Removed
							</Typography>
							<IconButton onClick={handleDone} size="small">
								<CloseIcon />
							</IconButton>
						</div>

						{/* Body */}
						<Typography variant="body1">
							This pet record has been removed from future activation messages.
						</Typography>

						{/* Close Button */}
						<button
							onClick={handleDone}
							style={{
								border: "none",
								background: "#FF9500",
								color: "#fff",
								borderRadius: 9999,
								padding: "12px 24px",
								fontWeight: 700,
								fontSize: 16,
								cursor: "pointer",
							}}
						>
							Close
						</button>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}

export default RemoveRecordDialog;
